/** Shared lead form helpers for CreateLead and Initiate Deal modal */

export function toDatetimeLocalValue(value) {
  if (value == null || value === '') return '';
  const d = new Date(String(value).replace(' ', 'T'));
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function toBool(v) {
  if (typeof v === 'string') return ['1', 'true', 'yes'].includes(v.toLowerCase().trim());
  return v === true || v === 1;
}

export function normalizeStatusName(name) {
  return (name || '').toLowerCase().replace(/[\s_-]+/g, ' ').trim();
}

export function isFollowUpStatusSelected(statusOptions, statusId) {
  if (!statusId) return false;
  const status = statusOptions.find(
    (s) => s.id === statusId || String(s.id) === String(statusId)
  );
  const normalized = normalizeStatusName(status?.name);
  return normalized.includes('follow up') || normalized.startsWith('followup');
}

export function isLiveTransferStatusSelected(statusOptions, statusId) {
  if (!statusId) return false;
  const status = statusOptions.find(
    (s) => s.id === statusId || String(s.id) === String(statusId)
  );
  const normalized = normalizeStatusName(status?.name);
  return normalized.includes('live transfer');
}

/** Quick hydrate from list row while full lead record loads */
export function mapListLeadToFormPrefill(row) {
  if (!row?.id) return null;
  const leadData = {
    ...row,
    last_status_log: row.last_status_log ?? row.lastStatusLog,
  };
  const assigneeIds =
    Array.isArray(row.assignees) && row.assignees.length
      ? [row.assignees[0].id ?? row.assignees[0].uuid]
      : [];
  return {
    ...mapLeadApiToForm(leadData),
    assignee_ids: assigneeIds.filter(Boolean),
    attachment_ids: extractLeadAttachmentIds(row),
    attachments: Array.isArray(row.attachments) ? row.attachments : [],
  };
}

export function extractLeadAttachmentIds(leadData) {
  if (Array.isArray(leadData.attachments) && leadData.attachments.length > 0) {
    return leadData.attachments
      .map((a) => (a && typeof a === 'object' ? a.id : a))
      .filter(Boolean);
  }
  if (Array.isArray(leadData.attachment_ids)) {
    return leadData.attachment_ids.filter(Boolean);
  }
  return [];
}

export function mapLeadApiToForm(leadData) {
  const dba = leadData.dba && typeof leadData.dba === 'object' ? leadData.dba : {};
  // Backend key is actually "legalIn_formation" (typo) — holds legal AND most dba/business fields.
  const li =
    leadData.legalIn_formation && typeof leadData.legalIn_formation === 'object'
      ? leadData.legalIn_formation
      : {};
  const legal =
    (leadData.legal_information && typeof leadData.legal_information === 'object' ? leadData.legal_information : null) ||
    (dba.legal_information && typeof dba.legal_information === 'object' ? dba.legal_information : null) ||
    li;
  const merchant =
    (leadData.merchant && typeof leadData.merchant === 'object' ? leadData.merchant : null) ||
    (dba.merchant && typeof dba.merchant === 'object' ? dba.merchant : {});
  const bank =
    (leadData.bank_account && typeof leadData.bank_account === 'object' ? leadData.bank_account : null) ||
    (dba.bank && typeof dba.bank === 'object' ? dba.bank : {});
  const rawEquip =
    (Array.isArray(leadData.equipment) && leadData.equipment.length ? leadData.equipment : null) ||
    (Array.isArray(leadData.equipments) && leadData.equipments.length ? leadData.equipments : null) ||
    (Array.isArray(dba.equipments) && dba.equipments.length ? dba.equipments : null) ||
    (Array.isArray(li.equipments) && li.equipments.length ? li.equipments : []);
  const eq0 = rawEquip.length ? rawEquip[0] : {};
  const resolveId = (v) => (v && typeof v === 'object' ? v.id : v) || '';
  const submission = leadData.submission_date || dba.submission_date || li.submission_date;

  return {
    dba_name: leadData.dba_name ?? dba.dba_name ?? dba.name ?? '',
    db_name: leadData.db_name ?? dba.db_name ?? '',
    name: leadData.name || '',
    email: leadData.email || '',
    phone: leadData.phone || '',
    address: leadData.address || '',
    description: leadData.description || '',
    status_id: leadData.last_status_log?.status?.id || '',
    source_id: leadData.source?.id || '',
    brand_id: leadData.brand?.id || '',
    country_id: leadData.country?.id || '',
    submission_date: submission ? String(submission).slice(0, 10) : '',
    legal_business_name: leadData.legal_business_name || legal.name || '',
    federal_tax_id: leadData.federal_tax_id || merchant.federal_tax_id || '',
    ssn: leadData.ssn ?? dba.ssn ?? li.ssn ?? '',
    ein: leadData.ein ?? dba.ein ?? li.ein ?? '',
    business_years: leadData.business_years ?? merchant.business_years ?? '',
    business_months: leadData.business_months ?? merchant.business_months ?? '',
    business_category: leadData.business_category ?? merchant.business_category ?? '',
    monthly_volume: leadData.monthly_volume ?? dba.monthly_volume ?? li.monthly_volume ?? '',
    equipment_type_id: resolveId(leadData.equipment_type_id || eq0.equipment_type_id || eq0.equipment_type),
    manufacturer_id: resolveId(leadData.manufacturer_id || eq0.manufacturer_id || eq0.manufacturer),
    equipment_model_id: resolveId(leadData.equipment_model_id || eq0.equipment_model_id || eq0.equipment_model),
    personal_phone: leadData.personal_phone ?? dba.phone ?? '',
    legal_business_phone: leadData.legal_business_phone || legal.phone || '',
    legal_business_address: leadData.legal_business_address || legal.address || '',
    legal_business_email: leadData.legal_business_email || legal.email || '',
    monthly_lease: leadData.monthly_lease ?? dba.monthly_lease ?? li.monthly_lease ?? '',
    is_bundled: toBool(leadData.is_bundled ?? dba.is_bundled ?? li.is_bundled),
    processing_offered: leadData.processing_offered ?? dba.processing_offered ?? li.processing_offered ?? '',
    avg_month_sales: leadData.avg_month_sales ?? merchant.avg_month_sales ?? '',
    highest_ticket: leadData.highest_ticket ?? dba.highest_ticket ?? li.highest_ticket ?? '',
    average_tickets:
      leadData.average_tickets ?? dba.average_ticket ?? merchant.average_ticket ?? li.average_ticket ?? '',
    bank_name: leadData.bank_name || bank.bank_name || '',
    routing_number: leadData.routing_number || bank.routing_number || '',
    account_number: leadData.account_number || bank.account_number || '',
    fns_number_for_ebt: leadData.fns_number_for_ebt ?? dba.fns_number_for_ebt ?? li.fns_number_for_ebt ?? '',
    dba_address: leadData.dba_address ?? dba.address ?? '',
    additional_note: leadData.additional_note ?? dba.additional_note ?? '',
    offer_services: normalizeOfferedServices(
      leadData.offer_services ??
        dba.offer_services ??
        leadData.offered_services ??
        dba.offered_services ??
        leadData.offerServices
    ),
    lease_id: resolveId(leadData.lease_id || leadData.lease || eq0.lease_id || eq0.lease),
    merchant_service_provider_id: resolveId(
      leadData.merchant_service_provider_id ||
        leadData.merchant_service_provider ||
        eq0.merchant_service_provider_id ||
        eq0.merchant_service_provider
    ),
    follow_up_date_time: toDatetimeLocalValue(
      leadData.follow_up_date_time ?? leadData.last_status_log?.date
    ),
  };
}

function putNum(target, key, val) {
  if (val === '' || val === undefined || val === null) return;
  const n = Number(val);
  if (!Number.isNaN(n)) target[key] = n;
}

export function createEmptyOfferedService() {
  return { id: undefined, service_content: '' };
}

export const OFFERED_SERVICE_MAX_LENGTH = 340;

/** Read display/edit text from an offer-service row (API or form). */
export function getOfferedServiceContent(item) {
  if (item == null) return '';
  if (typeof item === 'string') return item;
  const value =
    item?.service_content ??
    item?.service_offered ??
    '';
  return value == null ? '' : String(value);
}

export function normalizeOfferedServices(raw) {
  if (!Array.isArray(raw) || raw.length === 0) {
    return [createEmptyOfferedService()];
  }
  return raw.map((item) => {
    if (typeof item === 'string') {
      return {
        service_content: item.slice(0, OFFERED_SERVICE_MAX_LENGTH),
      };
    }
    // Only map real offer-service text fields — never fall back to name/title/
    // description (those can come from wrong related models and get re-saved).
    const content = getOfferedServiceContent(item).slice(0, OFFERED_SERVICE_MAX_LENGTH);
    return {
      ...(item?.id != null ? { id: item.id } : {}),
      service_content: content,
    };
  });
}

export function serializeOfferedServices(list = []) {
  return (Array.isArray(list) ? list : [])
    .map((item) => {
      const service_content = getOfferedServiceContent(item)
        .trim()
        .slice(0, OFFERED_SERVICE_MAX_LENGTH);
      if (!service_content) return null;
      const row = { service_content };
      if (item && typeof item === 'object' && item.id != null) {
        row.id = item.id;
      }
      return row;
    })
    .filter(Boolean);
}

export function buildLeadSubmitPayload(formData, statusOptions = []) {
  const base = {
    dba_name: formData.dba_name,
    name: formData.name,
    email: formData.email,
    phone: formData.phone,
    address: formData.address,
    description: formData.description,
    status_id: formData.status_id,
    source_id: formData.source_id,
    brand_id: formData.brand_id,
    ...(formData.country_id ? { country_id: formData.country_id } : {}),
    assignees: formData.assignee_ids,
  };

  const dba = {};
  if (formData.submission_date) dba.submission_date = formData.submission_date;
  if (formData.personal_phone) dba.phone = formData.personal_phone;
  if (formData.dba_name) dba.dba_name = formData.dba_name;
  if (formData.db_name) dba.db_name = formData.db_name;
  if (formData.monthly_lease !== '' && formData.monthly_lease != null) dba.monthly_lease = formData.monthly_lease;
  if (formData.is_bundled) dba.is_bundled = true;
  if (formData.processing_offered) dba.processing_offered = formData.processing_offered;
  if (formData.monthly_volume) dba.monthly_volume = formData.monthly_volume;
  if (formData.highest_ticket) dba.highest_ticket = formData.highest_ticket;
  if (formData.average_tickets !== '' && formData.average_tickets != null) {
    dba.average_ticket = formData.average_tickets;
  }
  if (formData.fns_number_for_ebt) dba.fns_number_for_ebt = formData.fns_number_for_ebt;
  if (formData.dba_address) dba.address = formData.dba_address;
  if (formData.additional_note) dba.additional_note = formData.additional_note;
  if (formData.ssn) dba.ssn = formData.ssn;
  if (formData.ein) dba.ein = formData.ein;

  const legal_information = {};
  if (formData.legal_business_name) legal_information.name = formData.legal_business_name;
  if (formData.legal_business_phone) legal_information.phone = formData.legal_business_phone;
  if (formData.legal_business_address) legal_information.address = formData.legal_business_address;
  if (formData.legal_business_email) legal_information.email = formData.legal_business_email;

  const merchant = {};
  if (formData.federal_tax_id) merchant.federal_tax_id = formData.federal_tax_id;
  putNum(merchant, 'business_years', formData.business_years);
  putNum(merchant, 'business_months', formData.business_months);
  if (formData.business_category) merchant.business_category = formData.business_category;
  putNum(merchant, 'avg_month_sales', formData.avg_month_sales);

  const bank_account = {};
  if (formData.bank_name) bank_account.bank_name = formData.bank_name;
  if (formData.routing_number) bank_account.routing_number = formData.routing_number;
  if (formData.account_number) bank_account.account_number = formData.account_number;

  const equipmentRow = {};
  if (formData.equipment_type_id) equipmentRow.equipment_type_id = formData.equipment_type_id;
  if (formData.manufacturer_id) equipmentRow.manufacturer_id = formData.manufacturer_id;
  if (formData.equipment_model_id) equipmentRow.equipment_model_id = formData.equipment_model_id;
  if (formData.lease_id) equipmentRow.lease_id = formData.lease_id;
  if (formData.merchant_service_provider_id) {
    equipmentRow.merchant_service_provider_id = formData.merchant_service_provider_id;
  }

  const payload = { ...base };
  if (Object.keys(dba).length) payload.dba = dba;
  if (Object.keys(legal_information).length) payload.legal_information = legal_information;
  if (Object.keys(merchant).length) payload.merchant = merchant;
  if (Object.keys(bank_account).length) payload.bank_account = bank_account;
  if (Object.keys(equipmentRow).length) payload.equipment = [equipmentRow];
  if (Array.isArray(formData.attachment_ids)) {
    payload.attachments = [...formData.attachment_ids];
  }
  const offerServices = serializeOfferedServices(formData.offer_services);
  if (offerServices.length) {
    payload.offer_services = offerServices;
  }
  if (
    isFollowUpStatusSelected(statusOptions, formData.status_id) &&
    formData.follow_up_date_time
  ) {
    payload.follow_up_date_time = formData.follow_up_date_time;
  }
  return payload;
}

export const LEAD_FIELD_LABELS = {
  dba_name: 'DBA Name',
  db_name: 'DB name',
  name: 'Customer Name',
  email: 'Email',
  phone: 'Phone',
  address: 'Address',
  description: 'Description',
  status_id: 'Status',
  follow_up_date_time: 'Follow-up date & time',
  source_id: 'Source',
  brand_id: 'Brand',
  country_id: 'Country',
  assignee_ids: 'Assignee',
  submission_date: 'Submission date',
  legal_business_name: 'Legal business name',
  federal_tax_id: 'Federal tax ID',
  ssn: 'SSN',
  ein: 'EIN',
  business_years: 'Business years',
  business_months: 'Business months',
  business_category: 'Business category',
  monthly_volume: 'Monthly volume',
  equipment_type_id: 'Equipment type',
  manufacturer_id: 'Manufacturer',
  equipment_model_id: 'Equipment model',
  personal_phone: 'Personal phone',
  legal_business_phone: 'Legal business phone',
  legal_business_address: 'Legal business address',
  legal_business_email: 'Legal business email',
  monthly_lease: 'Monthly lease',
  is_bundled: 'Bundled',
  processing_offered: 'Processing offered',
  avg_month_sales: 'Average monthly sales',
  highest_ticket: 'Highest ticket',
  average_tickets: 'Average tickets',
  bank_name: 'Bank name',
  routing_number: 'Routing number',
  account_number: 'Account number',
  fns_number_for_ebt: 'FNS number for EBT',
  dba_address: 'DBA address',
  additional_note: 'Additional note',
  offer_services: 'Offered Services',
  lease_id: 'Lease',
  merchant_service_provider_id: 'Merchant service provider',
  attachment_ids: 'Attachments',
};

export function getLeadFieldLabel(fieldKey) {
  return LEAD_FIELD_LABELS[fieldKey] || fieldKey.replace(/_/g, ' ');
}

export function createEmptyLeadForm(user) {
  return {
    dba_name: '',
    db_name: '',
    name: '',
    email: '',
    phone: '',
    address: '',
    description: '',
    status_id: '',
    follow_up_date_time: '',
    source_id: '',
    brand_id: '',
    country_id: '',
    assignee_ids: user?.id ? [user.id] : [],
    submission_date: '',
    legal_business_name: '',
    federal_tax_id: '',
    ssn: '',
    ein: '',
    business_years: '',
    business_months: '',
    business_category: '',
    monthly_volume: '',
    equipment_type_id: '',
    manufacturer_id: '',
    equipment_model_id: '',
    personal_phone: '',
    legal_business_phone: '',
    legal_business_address: '',
    legal_business_email: '',
    monthly_lease: '',
    is_bundled: false,
    processing_offered: '',
    avg_month_sales: '',
    highest_ticket: '',
    average_tickets: '',
    bank_name: '',
    routing_number: '',
    account_number: '',
    fns_number_for_ebt: '',
    dba_address: '',
    additional_note: '',
    offer_services: [createEmptyOfferedService()],
    lease_id: '',
    merchant_service_provider_id: '',
    attachment_ids: [],
    attachments: [],
  };
}

export function isValidEmail(value) {
  return /\S+@\S+\.\S+/.test(value);
}

export function validateLeadForm(formData, statusOptions) {
  const newErrors = {};
  if (!formData.dba_name?.trim()) newErrors.dba_name = 'DBA Name is required';
  if (!formData.name?.trim()) newErrors.name = 'Name is required';
  if (!formData.email?.trim()) newErrors.email = 'Email is required';
  else if (!isValidEmail(formData.email)) newErrors.email = 'Email is invalid';
  if (formData.legal_business_email && !isValidEmail(formData.legal_business_email)) {
    newErrors.legal_business_email = 'Email is invalid';
  }
  if (!formData.phone?.trim()) newErrors.phone = 'Phone is required';
  if (!formData.status_id) newErrors.status_id = 'Status is required';
  if (
    isFollowUpStatusSelected(statusOptions, formData.status_id) &&
    !formData.follow_up_date_time
  ) {
    newErrors.follow_up_date_time = 'Follow-up date & time is required';
  }
  if (!formData.source_id) newErrors.source_id = 'Source is required';
  if (!formData.brand_id) newErrors.brand_id = 'Brand is required';
  return newErrors;
}

export function capitalizeWords(text) {
  if (!text) return '';
  return text.replace(/\b\w/g, (char) => char.toUpperCase());
}
